import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "loading", "error", "errorMessage", "noIntegrations", "content",
    "heyreachSection", "heyreachList", "heyreachFilters", "heyreachCount",
    "instantlySection", "instantlyList", "instantlyFilters", "instantlyCount",
    "newDropZone", "stagedList", "submitButton"
  ]
  static values = { url: String, submitUrl: String }

  connect() {
    this.allCampaigns = { heyreach: [], instantly: [] }
    this.availableCampaigns = { heyreach: [], instantly: [] }
    this.activeFilters = { heyreach: "all", instantly: "all" }
    this.stagedMappings = [] // [{ id, name, heyreach: null|campaign, instantly: null|campaign }]
    this.nextStagedId = 1
    this.currentDrag = null
    this.fetchCampaigns()
  }

  // ═══════════════════════════════════════════
  // Data fetching
  // ═══════════════════════════════════════════

  async fetchCampaigns() {
    this.showLoading()
    try {
      const resp = await fetch(this.urlValue, {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        throw new Error(body.error || `HTTP ${resp.status}`)
      }
      const data = await resp.json()
      this.allCampaigns.heyreach = (data.heyreach || []).map(c => ({ ...c, id: String(c.id) }))
      this.allCampaigns.instantly = (data.instantly || []).map(c => ({ ...c, id: String(c.id) }))
      this.availableCampaigns.heyreach = [...this.allCampaigns.heyreach]
      this.availableCampaigns.instantly = [...this.allCampaigns.instantly]
      this.renderAll()
    } catch (e) {
      this.showError(e.message)
    }
  }

  renderAll() {
    const hr = this.allCampaigns.heyreach
    const inst = this.allCampaigns.instantly

    if (hr.length === 0 && inst.length === 0) {
      this.showNoIntegrations()
      return
    }

    if (hr.length > 0) {
      this.heyreachSectionTarget.classList.remove("hidden")
      this.buildFilters("heyreach", hr)
    }
    if (inst.length > 0) {
      this.instantlySectionTarget.classList.remove("hidden")
      this.buildFilters("instantly", inst)
    }

    this.renderPlatformList("heyreach")
    this.renderPlatformList("instantly")
    this.hideLoading()
    this.contentTarget.classList.remove("hidden")
  }

  // ═══════════════════════════════════════════
  // Status filters
  // ═══════════════════════════════════════════

  buildFilters(platform, campaigns) {
    const statuses = [...new Set(campaigns.map(c => String(c.status || "unknown").toLowerCase()))]
    const container = platform === "heyreach" ? this.heyreachFiltersTarget : this.instantlyFiltersTarget

    const allBtn = this.filterButton("all", "All", platform, true)
    const statusBtns = statuses.map(s => this.filterButton(s, this.capitalize(s), platform, false))
    container.innerHTML = ""
    container.appendChild(allBtn)
    statusBtns.forEach(b => container.appendChild(b))
  }

  filterButton(status, label, platform, active) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.textContent = label
    btn.dataset.action = "click->campaign-mapper#filterPlatform"
    btn.dataset.platform = platform
    btn.dataset.status = status
    btn.className = active
      ? "px-2 py-0.5 text-xs rounded-full border border-theme-accent bg-theme-accent/10 text-theme-accent font-medium"
      : "px-2 py-0.5 text-xs rounded-full border border-theme-primary/30 text-theme-muted hover:border-theme-accent/40"
    return btn
  }

  filterPlatform(event) {
    const platform = event.target.dataset.platform
    const status = event.target.dataset.status
    this.activeFilters[platform] = status

    const container = platform === "heyreach" ? this.heyreachFiltersTarget : this.instantlyFiltersTarget
    container.querySelectorAll("button").forEach(btn => {
      const isActive = btn.dataset.status === status
      btn.className = isActive
        ? "px-2 py-0.5 text-xs rounded-full border border-theme-accent bg-theme-accent/10 text-theme-accent font-medium"
        : "px-2 py-0.5 text-xs rounded-full border border-theme-primary/30 text-theme-muted hover:border-theme-accent/40"
    })

    this.renderPlatformList(platform)
  }

  // ═══════════════════════════════════════════
  // Platform campaign list rendering
  // ═══════════════════════════════════════════

  renderPlatformList(platform) {
    const list = platform === "heyreach" ? this.heyreachListTarget : this.instantlyListTarget
    const countEl = platform === "heyreach" ? this.heyreachCountTarget : this.instantlyCountTarget
    const filter = this.activeFilters[platform]
    const available = this.availableCampaigns[platform]

    const filtered = filter === "all"
      ? available
      : available.filter(c => String(c.status || "unknown").toLowerCase() === filter)

    countEl.textContent = `${filtered.length} / ${available.length}`
    list.innerHTML = filtered.map(c => this.campaignPill(platform, c)).join("")
  }

  campaignPill(platform, c) {
    const isHR = platform === "heyreach"
    const tag = isHR ? "HR" : "IN"
    const badgeCls = isHR
      ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
      : "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
    const status = c.status ? this.esc(c.status) : ""
    return `<div class="flex items-center gap-2 px-2 py-1.5 rounded border border-theme-primary/20 bg-theme-card cursor-grab hover:border-theme-accent/40 transition-colors text-xs"
                 draggable="true"
                 data-action="dragstart->campaign-mapper#dragStart dragend->campaign-mapper#dragEnd"
                 data-platform="${this.esc(platform)}"
                 data-campaign-id="${this.esc(c.id)}"
                 data-campaign-name="${this.esc(c.name || c.id)}"
                 data-campaign-status="${this.esc(c.status || "")}">
        <span class="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-semibold flex-shrink-0 ${badgeCls}">${tag}</span>
        <span class="truncate flex-1 font-medium text-theme-primary">${this.esc(c.name || c.id)}</span>
        ${status ? `<span class="text-[10px] text-theme-muted flex-shrink-0 uppercase">${status}</span>` : ""}
    </div>`
  }

  // ═══════════════════════════════════════════
  // Drag & Drop — source
  // ═══════════════════════════════════════════

  dragStart(event) {
    const el = event.currentTarget
    const data = {
      platform: el.dataset.platform,
      id: el.dataset.campaignId,
      name: el.dataset.campaignName,
      status: el.dataset.campaignStatus
    }
    event.dataTransfer.setData("application/campaign", JSON.stringify(data))
    event.dataTransfer.effectAllowed = "move"
    el.classList.add("opacity-40")
    this.currentDrag = data
  }

  dragEnd(event) {
    event.currentTarget.classList.remove("opacity-40")
    this.currentDrag = null
  }

  // ═══════════════════════════════════════════
  // Drag & Drop — "New Campaign" drop zone
  // ═══════════════════════════════════════════

  dragOverNew(event) { event.preventDefault(); event.dataTransfer.dropEffect = "move" }

  dragEnterNew(event) {
    event.preventDefault()
    this.newDropZoneTarget.classList.add("border-theme-accent", "bg-theme-accent/5")
    this.newDropZoneTarget.classList.remove("border-theme-primary/30")
  }

  dragLeaveNew(event) {
    if (!this.newDropZoneTarget.contains(event.relatedTarget)) {
      this.newDropZoneTarget.classList.remove("border-theme-accent", "bg-theme-accent/5")
      this.newDropZoneTarget.classList.add("border-theme-primary/30")
    }
  }

  dropNew(event) {
    event.preventDefault()
    this.newDropZoneTarget.classList.remove("border-theme-accent", "bg-theme-accent/5")
    this.newDropZoneTarget.classList.add("border-theme-primary/30")

    const campaign = this.extractDragData(event)
    if (!campaign) return

    this.removeCampaignFromAvailable(campaign.platform, campaign.id)

    const mapping = {
      id: this.nextStagedId++,
      name: campaign.name,
      heyreach: campaign.platform === "heyreach" ? campaign : null,
      instantly: campaign.platform === "instantly" ? campaign : null
    }
    this.stagedMappings.push(mapping)
    this.renderStagedList()
    this.renderPlatformList(campaign.platform)
    this.updateSubmitButton()
  }

  // ═══════════════════════════════════════════
  // Drag & Drop — staged mapping drop zones
  // ═══════════════════════════════════════════

  dragOverStaged(event) {
    event.preventDefault()
    if (this.currentDrag) {
      event.dataTransfer.dropEffect = this.canDropOnStaged(event.currentTarget, this.currentDrag) ? "move" : "none"
    }
  }

  dragEnterStaged(event) {
    event.preventDefault()
    const el = event.currentTarget

    if (this.currentDrag && !this.canDropOnStaged(el, this.currentDrag)) {
      el.classList.add("border-red-400", "bg-red-50", "dark:bg-red-900/10")
      el.classList.remove("border-theme-primary/20")
    } else {
      el.classList.add("border-theme-accent", "bg-theme-accent/5")
      el.classList.remove("border-theme-primary/20")
    }
  }

  dragLeaveStaged(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      this.resetStagedBorder(event.currentTarget)
    }
  }

  dropStaged(event) {
    event.preventDefault()
    const el = event.currentTarget
    this.resetStagedBorder(el)

    const campaign = this.extractDragData(event)
    if (!campaign) return

    const mappingId = parseInt(el.dataset.mappingId, 10)
    const mapping = this.stagedMappings.find(m => m.id === mappingId)
    if (!mapping) return

    if (!this.canDropOnMapping(mapping, campaign)) return

    this.removeCampaignFromAvailable(campaign.platform, campaign.id)
    mapping[campaign.platform] = campaign
    this.renderStagedList()
    this.renderPlatformList(campaign.platform)
    this.updateSubmitButton()
  }

  canDropOnStaged(el, campaign) {
    const mappingId = parseInt(el.dataset.mappingId, 10)
    const mapping = this.stagedMappings.find(m => m.id === mappingId)
    return mapping ? this.canDropOnMapping(mapping, campaign) : false
  }

  canDropOnMapping(mapping, campaign) {
    return mapping[campaign.platform] === null
  }

  resetStagedBorder(el) {
    el.classList.remove("border-theme-accent", "bg-theme-accent/5", "border-red-400", "bg-red-50", "dark:bg-red-900/10")
    el.classList.add("border-theme-primary/20")
  }

  // ═══════════════════════════════════════════
  // Staged mapping rendering
  // ═══════════════════════════════════════════

  renderStagedList() {
    this.stagedListTarget.innerHTML = this.stagedMappings.map(m => this.stagedCard(m)).join("")
  }

  stagedCard(mapping) {
    const hrSlot = mapping.heyreach
      ? this.attachedPill("heyreach", mapping.heyreach, mapping.id)
      : `<span class="text-[10px] text-theme-light italic px-2">No HeyReach</span>`

    const inSlot = mapping.instantly
      ? this.attachedPill("instantly", mapping.instantly, mapping.id)
      : `<span class="text-[10px] text-theme-light italic px-2">No Instantly</span>`

    return `<div class="rounded-lg border border-theme-primary/20 bg-theme-card p-3 transition-colors"
                 data-mapping-id="${mapping.id}"
                 data-action="dragover->campaign-mapper#dragOverStaged drop->campaign-mapper#dropStaged dragenter->campaign-mapper#dragEnterStaged dragleave->campaign-mapper#dragLeaveStaged">
      <div class="flex items-center gap-2 mb-2">
        <input type="text"
               class="flex-1 px-2 py-1 input-theme rounded text-sm focus:outline-none focus:ring-1 focus:ring-theme-accent"
               value="${this.esc(mapping.name)}"
               data-mapping-id="${mapping.id}"
               data-action="input->campaign-mapper#updateStagedName"
               placeholder="Campaign name">
        <button type="button"
                data-action="click->campaign-mapper#removeStaged"
                data-mapping-id="${mapping.id}"
                class="text-theme-muted hover:text-red-500 transition-colors p-0.5"
                title="Remove mapping">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
        </button>
      </div>
      <div class="flex gap-2">
        <div class="flex-1">${hrSlot}</div>
        <div class="flex-1">${inSlot}</div>
      </div>
    </div>`
  }

  attachedPill(platform, campaign, mappingId) {
    const isHR = platform === "heyreach"
    const tag = isHR ? "HR" : "IN"
    const wrapCls = isHR
      ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
      : "bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800"
    const labelCls = isHR
      ? "text-blue-600 dark:text-blue-400"
      : "text-purple-600 dark:text-purple-400"
    const btnCls = isHR
      ? "text-blue-400 hover:text-blue-600 dark:hover:text-blue-300"
      : "text-purple-400 hover:text-purple-600 dark:hover:text-purple-300"
    return `<div class="flex items-center gap-1.5 px-2 py-1 rounded ${wrapCls} text-xs">
      <span class="font-semibold ${labelCls} flex-shrink-0">${tag}</span>
      <span class="truncate flex-1 text-theme-primary">${this.esc(campaign.name)}</span>
      <button type="button"
              data-action="click->campaign-mapper#detachCampaign"
              data-mapping-id="${mappingId}"
              data-platform="${platform}"
              class="${btnCls} flex-shrink-0 transition-colors"
              title="Remove">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>
    </div>`
  }

  // ═══════════════════════════════════════════
  // Staged mapping actions
  // ═══════════════════════════════════════════

  updateStagedName(event) {
    const id = parseInt(event.target.dataset.mappingId, 10)
    const mapping = this.stagedMappings.find(m => m.id === id)
    if (mapping) mapping.name = event.target.value.trim()
    this.updateSubmitButton()
  }

  detachCampaign(event) {
    const btn = event.currentTarget
    const id = parseInt(btn.dataset.mappingId, 10)
    const platform = btn.dataset.platform
    const mapping = this.stagedMappings.find(m => m.id === id)
    if (!mapping || !mapping[platform]) return

    this.returnCampaignToAvailable(platform, mapping[platform])
    mapping[platform] = null

    if (!mapping.heyreach && !mapping.instantly) {
      this.stagedMappings = this.stagedMappings.filter(m => m.id !== id)
    }

    this.renderStagedList()
    this.renderPlatformList(platform)
    this.updateSubmitButton()
  }

  removeStaged(event) {
    const id = parseInt(event.currentTarget.dataset.mappingId, 10)
    const mapping = this.stagedMappings.find(m => m.id === id)
    if (!mapping) return

    if (mapping.heyreach) this.returnCampaignToAvailable("heyreach", mapping.heyreach)
    if (mapping.instantly) this.returnCampaignToAvailable("instantly", mapping.instantly)

    this.stagedMappings = this.stagedMappings.filter(m => m.id !== id)
    this.renderStagedList()
    this.renderPlatformList("heyreach")
    this.renderPlatformList("instantly")
    this.updateSubmitButton()
  }

  // ═══════════════════════════════════════════
  // Available campaigns bookkeeping
  // ═══════════════════════════════════════════

  removeCampaignFromAvailable(platform, campaignId) {
    this.availableCampaigns[platform] = this.availableCampaigns[platform].filter(c => c.id !== campaignId)
  }

  returnCampaignToAvailable(platform, campaign) {
    if (!this.availableCampaigns[platform].find(c => c.id === campaign.id)) {
      this.availableCampaigns[platform].push(campaign)
      this.availableCampaigns[platform].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    }
  }

  // ═══════════════════════════════════════════
  // Submit
  // ═══════════════════════════════════════════

  updateSubmitButton() {
    const valid = this.stagedMappings.length > 0 &&
      this.stagedMappings.every(m => m.name && (m.heyreach || m.instantly))

    this.submitButtonTarget.disabled = !valid
    this.submitButtonTarget.classList.toggle("opacity-50", !valid)
    this.submitButtonTarget.classList.toggle("cursor-not-allowed", !valid)
  }

  async submit() {
    const valid = this.stagedMappings.filter(m => m.name && (m.heyreach || m.instantly))
    if (valid.length === 0) return

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = "Setting up campaigns..."

    const campaigns = valid.map(m => {
      const mappings = []
      if (m.heyreach) {
        mappings.push({ platform_slug: "heyreach", platform_campaign_id: m.heyreach.id, platform_campaign_name: m.heyreach.name })
      }
      if (m.instantly) {
        mappings.push({ platform_slug: "instantly", platform_campaign_id: m.instantly.id, platform_campaign_name: m.instantly.name })
      }
      return { name: m.name, mappings }
    })

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/vnd.turbo-stream.html, application/json, text/html",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ campaigns }),
        redirect: "follow"
      })

      if (resp.redirected) {
        Turbo.visit(resp.url)
        return
      }

      if (resp.ok) {
        const contentType = resp.headers.get("content-type") || ""
        if (contentType.includes("text/vnd.turbo-stream")) {
          const html = await resp.text()
          Turbo.renderStreamMessage(html)
        } else if (contentType.includes("application/json")) {
          const data = await resp.json()
          if (data.redirect_url) {
            Turbo.visit(data.redirect_url)
          }
        } else {
          const html = await resp.text()
          Turbo.renderStreamMessage(html)
        }
      } else {
        this.showError(`Setup failed (${resp.status})`)
        this.resetSubmitButton()
      }
    } catch (e) {
      this.showError(e.message)
      this.resetSubmitButton()
    }
  }

  // ═══════════════════════════════════════════
  // Helpers
  // ═══════════════════════════════════════════

  extractDragData(event) {
    try { return JSON.parse(event.dataTransfer.getData("application/campaign")) } catch { return null }
  }

  showLoading() {
    this.loadingTarget.classList.remove("hidden")
    this.errorTarget.classList.add("hidden")
    this.noIntegrationsTarget.classList.add("hidden")
    this.contentTarget.classList.add("hidden")
  }

  hideLoading() { this.loadingTarget.classList.add("hidden") }

  showError(msg) {
    this.loadingTarget.classList.add("hidden")
    this.contentTarget.classList.add("hidden")
    this.noIntegrationsTarget.classList.add("hidden")
    this.errorTarget.classList.remove("hidden")
    this.errorMessageTarget.textContent = msg
  }

  showNoIntegrations() {
    this.loadingTarget.classList.add("hidden")
    this.errorTarget.classList.add("hidden")
    this.contentTarget.classList.add("hidden")
    this.noIntegrationsTarget.classList.remove("hidden")
  }

  resetSubmitButton() {
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.textContent = "Setup Campaigns"
  }

  esc(str) {
    const d = document.createElement("div")
    d.textContent = String(str ?? "")
    return d.innerHTML
  }

  capitalize(s) { return s.charAt(0).toUpperCase() + s.slice(1) }
}
